import {useFormik} from 'formik'
import {useState} from 'react'
import * as Yup from 'yup'
import AsyncSelect from 'react-select/async'
import useBookings from '../../hooks/useBookings'

type Scope = 'category' | 'practitioner' | 'listing'
type ListingType = 'class' | 'service'

export interface BookingFeeFormValues {
  scope: Scope
  category: string
  practitioner: string
  practitionerLabel: string
  listingType: ListingType
  listing: string
  listingLabel: string
  value: number
  type: string
  startDate: string
  endDate: string
  active: boolean
  note: string
}

interface BookingFeeFormProps {
  title: string
  categories: any[]
  initialValues: BookingFeeFormValues
  submitLabel: string
  onSubmit: (values: BookingFeeFormValues) => Promise<void>
  isLoading: boolean
}

const schema = Yup.object().shape({
  scope: Yup.string().oneOf(['category', 'practitioner', 'listing']).required(),
  category: Yup.string().when('scope', {
    is: 'category',
    then: (s) => s.required('Category is required'),
  }),
  practitioner: Yup.string().when('scope', {
    is: 'practitioner',
    then: (s) => s.required('Pick a practitioner'),
  }),
  listing: Yup.string().when('scope', {
    is: 'listing',
    then: (s) => s.required('Pick a class or service'),
  }),
  value: Yup.number().required('Value is required'),
  type: Yup.string().required('Type is required'),
  startDate: Yup.string().nullable(),
  endDate: Yup.string()
    .nullable()
    .test('after-start', 'End date must be after the start date', function (endDate) {
      const {startDate} = this.parent
      if (!startDate || !endDate) return true
      return new Date(endDate) > new Date(startDate)
    }),
})

const BookingFeeForm = ({
  title,
  categories,
  initialValues,
  submitLabel,
  onSubmit,
  isLoading,
}: BookingFeeFormProps) => {
  const {searchPractitioners, searchClasses, searchServices} = useBookings()

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      await onSubmit(values)
    },
  })

  const [listingType, setListingType] = useState<ListingType>(
    initialValues.listingType || 'class'
  )

  const loadPractitionerOptions = async (input: string) => {
    if (!input || input.length < 2) return []
    const users = await searchPractitioners(input)
    return users.map((u: any) => ({
      value: u._id,
      label: `${u.firstName || ''} ${u.lastName || ''}`.trim() + (u.email ? ` (${u.email})` : ''),
    }))
  }

  const loadListingOptions = async (input: string) => {
    if (!input || input.length < 2) return []
    if (listingType === 'class') {
      const classes = await searchClasses(input)
      return classes.map((c: any) => ({value: c._id, label: c.name}))
    }
    const services = await searchServices(input)
    return services.map((s: any) => ({
      value: s._id,
      label:
        s.serviceName +
        (s.belongsTo ? ` (${s.belongsTo.firstName || ''} ${s.belongsTo.lastName || ''})`.trim() : ''),
    }))
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>{title}</h3>
        </div>
      </div>

      <div className='collapse show'>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          <div className='card-body border-top p-9'>
            {/* Scope */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span className='required'>Applies to</span>
              </label>
              <div className='col-lg-8 fv-row d-flex gap-6'>
                {[
                  {value: 'category', label: 'Whole category (default)'},
                  {value: 'practitioner', label: 'One practitioner'},
                  {value: 'listing', label: 'One class or service'},
                ].map((opt) => (
                  <div key={opt.value} className='form-check form-check-custom'>
                    <input
                      type='radio'
                      className='form-check-input'
                      name='scope'
                      checked={formik.values.scope === opt.value}
                      onChange={() => formik.setFieldValue('scope', opt.value)}
                    />
                    <label className='form-check-label'>{opt.label}</label>
                  </div>
                ))}
              </div>
              <div className='col-lg-8 offset-lg-4 text-muted fs-7 mt-2'>
                If more than one rule could apply to a booking, the most specific one wins:
                a class/service rule beats a practitioner rule, which beats the category
                default.
              </div>
            </div>

            {/* Category (scope = category) */}
            {formik.values.scope === 'category' && (
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label fw-bold fs-6'>
                  <span className='required'>Category</span>
                </label>
                <div className='col-lg-8 fv-row'>
                  <select
                    className='form-select form-select-lg form-select-solid'
                    {...formik.getFieldProps('category')}
                  >
                    <option value=''>Select a category</option>
                    {categories.map((category: any) => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {formik.touched.category && formik.errors.category && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.category as string}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Practitioner (scope = practitioner) */}
            {formik.values.scope === 'practitioner' && (
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label fw-bold fs-6'>
                  <span className='required'>Practitioner</span>
                </label>
                <div className='col-lg-8 fv-row'>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions={false}
                    placeholder='Type a name or email to search...'
                    loadOptions={loadPractitionerOptions}
                    defaultValue={
                      formik.values.practitioner
                        ? {value: formik.values.practitioner, label: formik.values.practitionerLabel}
                        : null
                    }
                    onChange={(opt: any) => {
                      formik.setFieldValue('practitioner', opt?.value || '')
                      formik.setFieldValue('practitionerLabel', opt?.label || '')
                    }}
                  />
                  {formik.touched.practitioner && formik.errors.practitioner && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.practitioner as string}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Listing (scope = listing) */}
            {formik.values.scope === 'listing' && (
              <>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Type</span>
                  </label>
                  <div className='col-lg-8 fv-row d-flex gap-6'>
                    {(['class', 'service'] as ListingType[]).map((lt) => (
                      <div key={lt} className='form-check form-check-custom'>
                        <input
                          type='radio'
                          className='form-check-input'
                          name='listingType'
                          checked={listingType === lt}
                          onChange={() => {
                            setListingType(lt)
                            formik.setFieldValue('listingType', lt)
                            formik.setFieldValue('listing', '')
                            formik.setFieldValue('listingLabel', '')
                          }}
                        />
                        <label className='form-check-label text-capitalize'>{lt}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>{listingType === 'class' ? 'Class' : 'Service'}</span>
                  </label>
                  <div className='col-lg-8 fv-row'>
                    <AsyncSelect
                      key={listingType}
                      cacheOptions
                      defaultOptions={false}
                      placeholder={`Type a ${listingType} name to search...`}
                      loadOptions={loadListingOptions}
                      defaultValue={
                        formik.values.listing
                          ? {value: formik.values.listing, label: formik.values.listingLabel}
                          : null
                      }
                      onChange={(opt: any) => {
                        formik.setFieldValue('listing', opt?.value || '')
                        formik.setFieldValue('listingLabel', opt?.label || '')
                      }}
                    />
                    {formik.touched.listing && formik.errors.listing && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.listing as string}</div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Value */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span className='required'>Value</span>
              </label>
              <div className='col-lg-8 fv-row'>
                <input
                  type='number'
                  step='0.01'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='value'
                  {...formik.getFieldProps('value')}
                />
                {formik.touched.value && formik.errors.value && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.value as string}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Type */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span className='required'>Type</span>
              </label>
              <div className='col-lg-8 fv-row flex-row d-flex gap-6'>
                <div>
                  <input
                    type='checkbox'
                    className='form-check-input'
                    checked={formik.values.type === 'flat_fee'}
                    onChange={() => formik.setFieldValue('type', 'flat_fee')}
                  />
                  <span className='form-check-label fw-bold fs-6 mx-2'>Flat Fee</span>
                </div>
                <div>
                  <input
                    type='checkbox'
                    className='form-check-input'
                    checked={formik.values.type === 'percentage'}
                    onChange={() => formik.setFieldValue('type', 'percentage')}
                  />
                  <span className='form-check-label fw-bold fs-6 mx-3'>Percentage</span>
                </div>
                {formik.touched.type && formik.errors.type && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.type as string}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Scheduling */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                Start date <span className='text-muted fs-8'>(optional)</span>
              </label>
              <div className='col-lg-8 fv-row'>
                <input
                  type='date'
                  className='form-control form-control-lg form-control-solid'
                  {...formik.getFieldProps('startDate')}
                />
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                End date <span className='text-muted fs-8'>(optional)</span>
              </label>
              <div className='col-lg-8 fv-row'>
                <input
                  type='date'
                  className='form-control form-control-lg form-control-solid'
                  {...formik.getFieldProps('endDate')}
                />
                {formik.touched.endDate && formik.errors.endDate && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.endDate as string}</div>
                  </div>
                )}
                <div className='text-muted fs-7 mt-2'>
                  Leave both blank for a rule that applies indefinitely. Outside this window,
                  the rule is ignored and the next-most-general rule applies instead.
                </div>
              </div>
            </div>

            {/* Active */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Active</label>
              <div className='col-lg-8 fv-row'>
                <div className='form-check form-switch form-check-custom'>
                  <input
                    type='checkbox'
                    className='form-check-input'
                    checked={formik.values.active}
                    onChange={() => formik.setFieldValue('active', !formik.values.active)}
                  />
                </div>
                <div className='text-muted fs-7 mt-2'>
                  Turn off to retire this rule without deleting it.
                </div>
              </div>
            </div>

            {/* Note */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                Note <span className='text-muted fs-8'>(optional)</span>
              </label>
              <div className='col-lg-8 fv-row'>
                <textarea
                  className='form-control form-control-lg form-control-solid'
                  placeholder='e.g. spring retreat promo'
                  rows={2}
                  {...formik.getFieldProps('note')}
                />
              </div>
            </div>
          </div>

          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button type='submit' disabled={isLoading} className='btn btn-primary'>
              {!isLoading && submitLabel}
              {isLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default BookingFeeForm
